import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserName, updateUserName, getUserEmail, getUserId, getChangePasswordUrl, logout } from '../services/auth';
import { api } from '../services/api';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useTier } from '../hooks/useTier';
import { UpgradeButton } from './UpgradeButton';
import { Modal } from './index';

interface ProfileSettingsProps {
  searchId: string | null;
  onBack: () => void;
}

function SavedIndicator({ visible, label }: { visible: boolean; label: string }) {
  return (
    <span className={`settings-saved-indicator ${visible ? 'visible' : ''}`}>
      {label}
    </span>
  );
}

export function ProfileSettings({ searchId, onBack }: ProfileSettingsProps) {
  const { t } = useTranslation();
  // Account
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const nameTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Notifications
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [notifSaved, setNotifSaved] = useState(false);
  const notifTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Subscription
  const { tier, loading: tierLoading } = useTier();
  const [portalLoading, setPortalLoading] = useState(false);

  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(nameTimerRef.current);
      clearTimeout(debounceRef.current);
      clearTimeout(notifTimerRef.current);
    };
  }, []);

  // Load user info
  useEffect(() => {
    getUserEmail().then(setEmail);
    getUserId().then(setUserId);
    getUserName().then((name) => {
      const n = name || '';
      setDisplayName(n);
      setOriginalName(n);
    });
  }, []);

  // Load notification preferences
  useEffect(() => {
    if (!searchId || !userId) return;
    api.getMemberPreferences(searchId, userId).then(({ preferences }) => {
      if (!Array.isArray(preferences)) {
        setEmailNotifications(preferences.emailNotificationsEnabled ?? false);
      }
    }).catch(() => {});
  }, [searchId, userId]);

  // Auto-save display name
  const saveName = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === originalName) return;
    setSavingName(true);
    setNameError(null);
    try {
      await updateUserName(trimmed);
      setOriginalName(trimmed);
      setNameSaved(true);
      clearTimeout(nameTimerRef.current);
      nameTimerRef.current = setTimeout(() => setNameSaved(false), 2000);
    } catch {
      setNameError('Failed to update name');
    } finally {
      setSavingName(false);
    }
  }, [originalName]);

  const handleNameChange = (value: string) => {
    setDisplayName(value);
    setNameError(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveName(value), 500);
  };

  const handleNameBlur = () => {
    clearTimeout(debounceRef.current);
    saveName(displayName);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      clearTimeout(debounceRef.current);
      saveName(displayName);
    }
  };

  // Notifications toggle
  const handleNotifToggle = async () => {
    if (!searchId || !userId) return;
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    try {
      await api.updateMemberPreferences(searchId, userId, { emailNotificationsEnabled: newValue });
      setNotifSaved(true);
      clearTimeout(notifTimerRef.current);
      notifTimerRef.current = setTimeout(() => setNotifSaved(false), 2000);
    } catch {
      setEmailNotifications(!newValue); // revert
    }
  };

  // Subscription
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { url } = await api.createPortalSession();
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = () => {
    const subject = encodeURIComponent('Account Deletion Request');
    const body = encodeURIComponent(`Hi MoveTwo team,\n\nI would like to request the deletion of my account.\n\nEmail: ${email || '(not available)'}\n\nThank you.`);
    window.open(`mailto:support@movetwo.app?subject=${subject}&body=${body}`, '_self');
    setShowDeleteModal(false);
  };

  return (
    <div className="settings-page">
      <button className="btn btn-ghost btn-sm back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> {t('common.back')}
      </button>

      <h2 className="settings-heading">{t('app.profileSettings.heading')}</h2>

      {/* ─── Account ─── */}
      <section className="settings-card">
        <h3 className="settings-section-title">{t('app.profileSettings.accountSection')}</h3>

        <div className="settings-field-row">
          <span className="settings-field-label">{t('app.profileSettings.emailLabel')}</span>
          <span className="settings-field-value">{email || '...'}</span>
        </div>

        <div className="settings-field-row">
          <label className="settings-field-label" htmlFor="display-name">{t('app.profileSettings.displayNameLabel')}</label>
          <div className="settings-name-input-wrap">
            <input
              id="display-name"
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              placeholder={t('app.profileSettings.displayNamePlaceholder')}
              disabled={savingName}
            />
            <SavedIndicator visible={nameSaved} label={t('app.searchSettings.saved')} />
            {nameError && <span className="settings-message error">{nameError}</span>}
          </div>
        </div>

        {getChangePasswordUrl() && (
          <a
            href={getChangePasswordUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="settings-link"
          >
            {t('app.profileSettings.changePassword')} <ExternalLink size={14} />
          </a>
        )}
      </section>

      {/* ─── Notifications ─── */}
      {searchId && userId && (
        <section className="settings-card">
          <h3 className="settings-section-title">{t('app.profileSettings.notificationsSection')}</h3>
          <div className="settings-toggle-row">
            <div className="settings-toggle-text">
              <span className="settings-toggle-label">{t('app.profileSettings.emailNotificationsLabel')}</span>
              <span className="settings-toggle-desc">
                {t('app.profileSettings.emailNotificationsDesc')}
              </span>
            </div>
            <div className="settings-toggle-control">
              <button
                role="switch"
                aria-checked={emailNotifications}
                className={`settings-toggle ${emailNotifications ? 'on' : ''}`}
                onClick={handleNotifToggle}
              >
                <span className="settings-toggle-thumb" />
              </button>
              <SavedIndicator visible={notifSaved} label={t('app.searchSettings.saved')} />
            </div>
          </div>
        </section>
      )}

      {/* ─── Subscription ─── */}
      {!tierLoading && tier && (
        <section className="settings-card">
          <h3 className="settings-section-title">{t('app.profileSettings.subscriptionSection')}</h3>
          <p className="settings-section-desc">
            {t('app.profileSettings.currentPlan', { plan: tier === 'pro' ? t('app.profileSettings.planPro') : tier === 'trial' ? t('app.profileSettings.planTrial') : t('app.profileSettings.planExpired') })}
          </p>
          {tier === 'pro' ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? t('common.loading') : t('app.profileSettings.manageSubscription')}
            </button>
          ) : (
            <UpgradeButton />
          )}
        </section>
      )}

      {/* ─── Danger Zone ─── */}
      <section className="settings-card settings-danger-zone">
        <h3 className="settings-section-title">{t('app.profileSettings.dangerZone')}</h3>
        <div className="settings-danger-actions">
          <div className="settings-danger-item">
            <div>
              <span className="settings-danger-label">{t('app.profileSettings.deleteAccountLabel')}</span>
              <span className="settings-danger-desc">{t('app.profileSettings.deleteAccountDesc')}</span>
            </div>
            <button
              className="btn btn-sm settings-delete-btn"
              onClick={() => setShowDeleteModal(true)}
            >
              {t('app.profileSettings.deleteAccount')}
            </button>
          </div>
          <div className="settings-danger-item">
            <div>
              <span className="settings-danger-label">{t('app.profileSettings.signOutLabel')}</span>
              <span className="settings-danger-desc">{t('app.profileSettings.signOutDesc')}</span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => logout()}>
              {t('app.profileSettings.signOutButton')}
            </button>
          </div>
        </div>
      </section>

      {/* Delete Account Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title={t('app.profileSettings.deleteAccountModal')}>
        <p style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--gray-600)' }}>
          {t('app.profileSettings.deleteAccountWarning')}
        </p>
        <p style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--gray-600)' }}>
          {t('app.profileSettings.deleteAccountConfirmation')}
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleteModal(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-sm settings-delete-btn" onClick={handleDeleteAccount}>
            {t('app.profileSettings.requestDeletion')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
